/* ═══════════════════════════════════════════════════════════════
   Spec Masters Studio — the Updates state (Phase 3).

   When the office master publishes, every client version gets a feed
   entry: which blocks moved, and what that means for THIS client's copy.
   This hook is the wiring between that feed and the Studio:

     · the entries for the master, split per client version
     · the pending count behind every badge (panel button, variant tab)
     · the block ids the sheet decorates and the block menu offers
     · the decision itself — server first (it holds the permission and
       rewrites the client's layer), then the SAME pure mutation applied
       to the open document so the sheet moves with the click

   The maths is never re-implemented here: `applyDecisionToLayer` from
   shared/variant-updates.ts is the one brain, and the server ran it too.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import type { ClientLayer } from '../../shared/types';
import {
  applyDecisionToLayer,
  isModuleEntry,
  pendingByBlock,
  pendingCount,
  type VariantChange,
  type VariantChangeEntry,
} from '../../shared/variant-updates';
import {
  applyModuleDecisionAllToLayer,
  applyModuleDecisionToLayer,
} from '../../shared/module-updates';
import { VariantChangeService } from '../services/variantChangeService';
import { reportError } from '../services/errorLog';
import { useTableSync } from '../hooks/useTableSync';
import type { SpecMasterDraftApi } from './useSpecMasterDraft';

export interface VariantUpdatesApi {
  /** Entries for the ACTIVE client version, newest publish first. */
  entries: VariantChangeEntry[];
  /** Blocks in the active version carrying an undecided change. */
  pendingIds: Set<string>;
  /** Undecided changes in the active version — the panel-button badge. */
  pendingTotal: number;
  /** Undecided changes for any version — the variant-tab pills. `null` asks the
   *  same of the OFFICE MASTER tab, where module updates land (Phase 6). */
  countFor: (layerId: string | null) => number;
  /** The change on one block, if it is still undecided. */
  changeFor: (blockId: string) => { entry: VariantChangeEntry; change: VariantChange } | null;
  canDecide: boolean;
  busy: boolean;
  /** Bumps whenever the feed moves — the canvas repaints its decorations. */
  version: number;
  /** A block's update tag was clicked: the panel scrolls to that card. */
  focusChangeId: string | null;
  openForBlock: (blockId: string) => void;
  clearFocus: () => void;
  decide: (entryId: string, changeId: string, decision: 'accepted' | 'rejected') => Promise<void>;
  decideAll: (entryId: string, decision: 'accepted' | 'rejected') => Promise<void>;
  /** The block menu's Accept / Reject update. */
  decideBlock: (blockId: string, decision: 'accepted' | 'rejected') => Promise<void>;
  reload: () => void;
}

export function useVariantUpdates(
  masterId: string,
  draft: SpecMasterDraftApi,
  activeLayerId: string | null,
  canDecide: boolean,
  toast: (msg: string, isError?: boolean) => void,
  onLayerChanged: () => void
): VariantUpdatesApi {
  const [all, setAll] = React.useState<VariantChangeEntry[]>([]);
  const [busy, setBusy] = React.useState(false);
  const [version, setVersion] = React.useState(0);
  const [focusChangeId, setFocusChangeId] = React.useState<string | null>(null);

  const toastRef = React.useRef(toast);
  toastRef.current = toast;
  const changedRef = React.useRef(onLayerChanged);
  changedRef.current = onLayerChanged;

  const load = React.useCallback(() => {
    if (!masterId) return;
    VariantChangeService.listForMaster(masterId)
      .then((rows) => {
        setAll(rows);
        setVersion((v) => v + 1);
      })
      .catch((err) => reportError('SpecStudio:updates', err));
  }, [masterId]);

  React.useEffect(() => load(), [load]);
  useTableSync(['spec_variant_changes'], load);

  /* What the panel shows depends on where you are standing:
       · a client version → everything aimed at that layer (a publish from the
         office master, and any module update that layer's own adds carry)
       · the OFFICE MASTER → module updates aimed at the master's core content
         and nothing else. A publish is meaningless there (it IS the publish),
         which is what the panel's empty state has always said. */
  const entries = React.useMemo(
    () =>
      activeLayerId
        ? all.filter((e) => e.layerId === activeLayerId)
        : all.filter((e) => isModuleEntry(e) && !e.layerId),
    [all, activeLayerId]
  );

  const pending = React.useMemo(() => pendingByBlock(entries), [entries]);
  const pendingIds = React.useMemo(() => new Set(pending.keys()), [pending]);
  const pendingTotal = React.useMemo(
    () => entries.reduce((n, e) => n + pendingCount(e), 0),
    [entries]
  );

  const countFor = React.useCallback(
    (layerId: string | null) =>
      all
        .filter((e) => (layerId ? e.layerId === layerId : isModuleEntry(e) && !e.layerId))
        .reduce((n, e) => n + pendingCount(e), 0),
    [all]
  );

  const changeFor = React.useCallback(
    (blockId: string) => {
      for (const entry of entries) {
        const change = (entry.changes || []).find(
          (c) => c.blockId === blockId && c.decision === 'pending'
        );
        if (change) return { entry, change };
      }
      return null;
    },
    [entries]
  );

  /** Apply the decided change to the OPEN document's layer, with the same
   *  pure function the server just ran — so the sheet agrees with the
   *  database without a round trip. Marks the draft dirty only when a draft
   *  is already open: answering an update must not silently check out (and
   *  lock) a master nobody was editing. */
  const applyLocally = React.useCallback(
    (layerId: string, change: VariantChange, decision: 'accepted' | 'rejected') => {
      draft.applyLayerDecision(layerId, (l: ClientLayer) =>
        applyDecisionToLayer(l, change, decision)
      );
      changedRef.current();
    },
    [draft]
  );

  /** The same job for a MODULE update (Phase 6). A layer's own copy is a pure
   *  layer rewrite like everything else; the master's core content was
   *  rewritten by the server OUTSIDE the draft flow, so the honest local move
   *  is to re-read the master rather than guess at the editor's document. */
  const applyModuleLocally = React.useCallback(
    (entry: VariantChangeEntry, changeId: string | null, decision: 'accepted' | 'rejected') => {
      if (!entry.layerId) {
        void draft.reload();
        return;
      }
      draft.applyLayerDecision(entry.layerId, (l: ClientLayer) =>
        changeId
          ? applyModuleDecisionToLayer(l, entry, changeId, decision).layer
          : applyModuleDecisionAllToLayer(l, entry, decision).layer
      );
      changedRef.current();
    },
    [draft]
  );

  /** A module update aimed at the office master lands on the PUBLISHED row.
   *  With a draft open the sheet is showing the draft's copy, so the change
   *  would look lost — say so instead of writing behind the user's back. */
  const blockedByDraft = React.useCallback(
    (entry: VariantChangeEntry): boolean => {
      if (!isModuleEntry(entry) || entry.layerId || !draft.draft) return false;
      toastRef.current(
        'Save or discard your draft first — a module update lands on the published master.',
        true
      );
      return true;
    },
    [draft]
  );

  const decide = React.useCallback(
    async (entryId: string, changeId: string, decision: 'accepted' | 'rejected') => {
      if (!canDecide) return;
      const entry = all.find((e) => e.id === entryId);
      const change = entry?.changes?.find((c) => c.changeId === changeId);
      if (!entry || !change || change.decision !== 'pending') return;
      if (blockedByDraft(entry)) return;
      setBusy(true);
      try {
        const saved = await VariantChangeService.decide(entryId, changeId, decision);
        setAll((rows) => rows.map((e) => (e.id === entryId ? saved || e : e)));
        if (isModuleEntry(entry)) applyModuleLocally(entry, changeId, decision);
        else if (entry.layerId) applyLocally(entry.layerId, change, decision);
        setVersion((v) => v + 1);
        toastRef.current(
          decision === 'accepted'
            ? 'Update accepted'
            : isModuleEntry(entry)
              ? 'Update rejected — this copy keeps what it has'
              : "Update rejected — this client's version keeps its own wording"
        );
      } catch (err) {
        reportError('SpecStudio:decideUpdate', err);
        toastRef.current(
          'Could not save that decision: ' + (err instanceof Error ? err.message : 'Unknown error'),
          true
        );
        load();
      } finally {
        setBusy(false);
      }
    },
    [all, applyLocally, applyModuleLocally, blockedByDraft, canDecide, load]
  );

  const decideAll = React.useCallback(
    async (entryId: string, decision: 'accepted' | 'rejected') => {
      if (!canDecide) return;
      const entry = all.find((e) => e.id === entryId);
      if (!entry) return;
      const pendingChanges = (entry.changes || []).filter((c) => c.decision === 'pending');
      if (!pendingChanges.length) return;
      if (blockedByDraft(entry)) return;
      setBusy(true);
      try {
        const saved = await VariantChangeService.decideAll(entryId, decision);
        setAll((rows) => rows.map((e) => (e.id === entryId ? saved || e : e)));
        if (isModuleEntry(entry)) {
          applyModuleLocally(entry, null, decision);
        } else {
          // One layer, every pending change, in the order the server used.
          if (entry.layerId) {
            draft.applyLayerDecision(entry.layerId, (l: ClientLayer) => {
              let out = l;
              for (const change of pendingChanges)
                out = applyDecisionToLayer(out, change, decision);
              return out;
            });
          }
          changedRef.current();
        }
        setVersion((v) => v + 1);
        toastRef.current(
          decision === 'accepted'
            ? `Accepted ${pendingChanges.length} update${pendingChanges.length === 1 ? '' : 's'}`
            : `Rejected ${pendingChanges.length} update${pendingChanges.length === 1 ? '' : 's'}`
        );
      } catch (err) {
        reportError('SpecStudio:decideUpdates', err);
        toastRef.current(
          'Could not save those decisions: ' +
            (err instanceof Error ? err.message : 'Unknown error'),
          true
        );
        load();
      } finally {
        setBusy(false);
      }
    },
    [all, applyModuleLocally, blockedByDraft, canDecide, draft, load]
  );

  const decideBlock = React.useCallback(
    async (blockId: string, decision: 'accepted' | 'rejected') => {
      const hit = changeFor(blockId);
      if (!hit) return;
      await decide(hit.entry.id, hit.change.changeId, decision);
    },
    [changeFor, decide]
  );

  const openForBlock = React.useCallback(
    (blockId: string) => {
      const hit = changeFor(blockId);
      setFocusChangeId(hit ? hit.change.changeId : null);
    },
    [changeFor]
  );

  return {
    entries,
    pendingIds,
    pendingTotal,
    countFor,
    changeFor,
    canDecide,
    busy,
    version,
    focusChangeId,
    openForBlock,
    clearFocus: React.useCallback(() => setFocusChangeId(null), []),
    decide,
    decideAll,
    decideBlock,
    reload: load,
  };
}
